import React, { useState } from "react";
import { Box, Typography, IconButton, Slide, Button, CircularProgress, TextField } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ReplayIcon from "@mui/icons-material/Replay";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import arrivalValidityRuleAPI from "../../Network/ArrivalValidityRule";
import DateFieldYMD from "../../Components/DateFieldYMD";

/* Local design tokens (UI_UX_AGENT_STANDARDS §F.2) */
const t = {
  surface: "#FFFFFF", surfaceMuted: "#F1F5F9", control: "#FFFFFF",
  border: "#CBD5E1", borderStrong: "#94A3B8", text: "#0F172A", textMuted: "#64748B",
  primary: "#1C64F2", primaryDark: "#1A56DB", success: "#10B981", danger: "#EF4444", warning: "#F59E0B",
};

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    bgcolor: t.control, borderRadius: 2, fontFamily: "Cairo", fontSize: 14,
    "& fieldset": { borderColor: t.border },
    "&:hover fieldset": { borderColor: t.borderStrong },
    "&.Mui-focused fieldset": { borderColor: t.primary, borderWidth: 2 },
  },
};

// Recompute arrival validity for a date range (by arrival date) for a given
// transaction lifecycle (1=external / 2=internal). Uses the same backend
// endpoint as the arrival-validity-rules page.
export default function RecalcValidityDialog({ open, setOpen, lifecycleType, lifecycleLabel, onDone }) {
  const [tt] = useTranslation("common");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [limit, setLimit] = useState(1000);
  const [running, setRunning] = useState(false);
  const [summary, setSummary] = useState(null);

  if (!open) return null;

  const run = async () => {
    if (!from || !to) { toast.warning(tt("ARRIVAL_VALIDITY_RULES.range_required")); return; }
    if (from > to) { toast.warning(tt("ARRIVAL_VALIDITY_RULES.to_before_from")); return; }
    setRunning(true); setSummary(null);
    const res = await arrivalValidityRuleAPI.recomputeArrivalValidity({
      data: {
        arrived_at_from: `${from} 00:00:00`,
        arrived_at_to: `${to} 23:59:59`,
        lifecycle_type: lifecycleType || null,
        limit: parseInt(limit) || 1000,
      },
    });
    setRunning(false);
    if (res?.status && res?.data?.status) {
      const s = res?.data?.data?.summary || res?.data?.data;
      setSummary(s);
      toast.success(tt("ARRIVAL_VALIDITY_RULES.recompute_done"));
      if (onDone) onDone();
    } else {
      const m = res?.data?.data?.message;
      toast.error((typeof m === "string" ? m : m?.text) || tt("NETWORK_MESSAGES.unknownError"));
    }
  };

  const stat = (label, value, color) => (
    <Box sx={{ flex: "1 1 30%", minWidth: 110, bgcolor: t.surfaceMuted, borderRadius: 2, p: 1.25, textAlign: "center" }}>
      <Typography sx={{ fontFamily: "Cairo-Bold", fontSize: 20, color: color || t.text }}>{value ?? 0}</Typography>
      <Typography sx={{ fontFamily: "Cairo", fontSize: 12, color: t.textMuted }}>{label}</Typography>
    </Box>
  );

  return (
    <Box
      onClick={(e) => { if (e.target === e.currentTarget && !running) setOpen(false); }}
      sx={{ position: "fixed", inset: 0, zIndex: 1300, display: "flex", alignItems: "center", justifyContent: "center", bgcolor: "rgba(15,23,42,0.55)", p: 2 }}
    >
      <Slide direction="down" in timeout={220}>
        <Box sx={{ bgcolor: t.surface, borderRadius: 3, border: `1px solid ${t.border}`, boxShadow: "0 20px 55px rgba(0,0,0,0.28)", width: "100%", maxWidth: 560, overflow: "hidden", display: "flex", flexDirection: "column", maxHeight: "90vh" }}>
          {/* header */}
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", p: 2.5, borderBottom: `1px solid ${t.border}` }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
              <Box sx={{ width: 38, height: 38, borderRadius: 2, bgcolor: "rgba(245,158,11,0.12)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <ReplayIcon sx={{ color: t.warning, fontSize: 22 }} />
              </Box>
              <Box>
                <Typography sx={{ fontFamily: "Cairo-Bold", fontSize: 16, color: t.text }}>
                  {tt("ARRIVAL_VALIDITY_RULES.recompute_title")}
                </Typography>
                {lifecycleLabel ? (
                  <Typography sx={{ fontFamily: "Cairo", fontSize: 12, color: t.textMuted }}>{lifecycleLabel}</Typography>
                ) : null}
              </Box>
            </Box>
            <IconButton onClick={() => !running && setOpen(false)} sx={{ color: t.textMuted, "&:hover": { bgcolor: t.surfaceMuted, color: t.text } }}>
              <CloseIcon />
            </IconButton>
          </Box>

          {/* body */}
          <Box sx={{ p: 3, overflowY: "auto" }}>
            <Typography sx={{ fontFamily: "Cairo", fontSize: 13, color: t.textMuted, mb: 2 }}>
              {tt("ARRIVAL_VALIDITY_RULES.recompute_hint")}
            </Typography>
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
              <Box>
                <Typography sx={{ fontSize: 13, fontWeight: 700, color: t.text, fontFamily: "Cairo-Medium", mb: 0.5 }}>{tt("ARRIVAL_VALIDITY_RULES.arrived_from")}</Typography>
                <DateFieldYMD value={from} onChange={setFrom} sx={fieldSx} />
              </Box>
              <Box>
                <Typography sx={{ fontSize: 13, fontWeight: 700, color: t.text, fontFamily: "Cairo-Medium", mb: 0.5 }}>{tt("ARRIVAL_VALIDITY_RULES.arrived_to")}</Typography>
                <DateFieldYMD value={to} onChange={setTo} sx={fieldSx} />
              </Box>
              <Box>
                <Typography sx={{ fontSize: 13, fontWeight: 700, color: t.text, fontFamily: "Cairo-Medium", mb: 0.5 }}>{tt("ARRIVAL_VALIDITY_RULES.limit")}</Typography>
                <TextField type="text" inputMode="numeric" value={limit} onChange={(e) => setLimit(e.target.value.replace(/[^\d]/g, ""))} size="small" fullWidth sx={fieldSx} inputProps={{ dir: "ltr", style: { textAlign: "left" } }} />
              </Box>
            </Box>

            {summary ? (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.25, mt: 2.5 }}>
                {stat(tt("ARRIVAL_VALIDITY_RULES.sum_candidates"), summary.total_candidates ?? summary.evaluated)}
                {stat(tt("ARRIVAL_VALIDITY_RULES.sum_marked_invalid"), summary.marked_invalid, t.danger)}
                {stat(tt("ARRIVAL_VALIDITY_RULES.sum_valid"), summary.valid, t.success)}
                {stat(tt("ARRIVAL_VALIDITY_RULES.sum_skipped"), summary.skipped)}
                {stat(tt("ARRIVAL_VALIDITY_RULES.sum_errors"), summary.errors, t.warning)}
              </Box>
            ) : null}
          </Box>

          {/* footer */}
          <Box sx={{ display: "flex", gap: 1.5, p: 2.5, borderTop: `1px solid ${t.border}` }}>
            <Button onClick={run} disabled={running} variant="contained" disableElevation
              startIcon={running ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : <ReplayIcon />}
              sx={{ bgcolor: t.warning, "&:hover": { bgcolor: "#D97706" }, fontFamily: "Cairo-Bold", borderRadius: 2, px: 3 }}>
              {running ? tt("ARRIVAL_VALIDITY_RULES.running") : tt("ARRIVAL_VALIDITY_RULES.run_recompute")}
            </Button>
            <Button onClick={() => setOpen(false)} disabled={running} variant="outlined"
              sx={{ color: t.text, borderColor: t.border, "&:hover": { borderColor: t.borderStrong, bgcolor: t.surfaceMuted }, fontFamily: "Cairo-Bold", borderRadius: 2, px: 3 }}>
              {tt("GLOBAL.cancel")}
            </Button>
          </Box>
        </Box>
      </Slide>
    </Box>
  );
}
